import type {
  KitBasketQuery,
  KitCorrectionListQuery,
  KitDemandForecastQuery,
  KitOutProtocolListQuery,
  KitShipmentListQuery,
  KitStockQuery,
  LabKitListQuery,
  TrialLaboratoryListQuery,
} from '@ctms/contracts';

/** Default query for trial laboratories (first page, up to 100). */
export const DEFAULT_TRIAL_LABORATORIES_QUERY: TrialLaboratoryListQuery = {
  page: 1,
  limit: 100,
};

/** Default query for lab kits (first page, up to 100). */
export const DEFAULT_LAB_KITS_QUERY: LabKitListQuery = {
  page: 1,
  limit: 100,
};

/** Default query for the kit demand forecast (first page, 8-week horizon). */
export const DEFAULT_KIT_DEMAND_FORECAST_QUERY: Omit<KitDemandForecastQuery, 'trialId'> = {
  page: 1,
  limit: 100,
  horizonWeeks: 8,
};

export const kitQueryKeys = {
  all: ['kits'] as const,
  laboratories: {
    all: ['kits', 'laboratories'] as const,
    lists: () => [...kitQueryKeys.laboratories.all, 'list'] as const,
    list: (query: TrialLaboratoryListQuery = DEFAULT_TRIAL_LABORATORIES_QUERY) =>
      [...kitQueryKeys.laboratories.lists(), query] as const,
    byTrial: (trialId: number) =>
      [...kitQueryKeys.laboratories.lists(), { trialId }] as const,
    detail: (id: number) => [...kitQueryKeys.laboratories.all, 'detail', id] as const,
  },
  kits: {
    all: ['kits', 'items'] as const,
    lists: () => [...kitQueryKeys.kits.all, 'list'] as const,
    list: (query: LabKitListQuery = DEFAULT_LAB_KITS_QUERY) =>
      [...kitQueryKeys.kits.lists(), query] as const,
    byLab: (trialLaboratoryId: number) =>
      [...kitQueryKeys.kits.lists(), { trialLaboratoryId }] as const,
    detail: (id: number) => [...kitQueryKeys.kits.all, 'detail', id] as const,
    expirations: (kitId: number) => [...kitQueryKeys.kits.all, 'expirations', kitId] as const,
  },
  shipments: {
    all: ['kits', 'shipments'] as const,
    lists: () => [...kitQueryKeys.shipments.all, 'list'] as const,
    list: (query: KitShipmentListQuery) =>
      [...kitQueryKeys.shipments.lists(), query] as const,
    byTrial: (trialId: number) =>
      [...kitQueryKeys.shipments.lists(), { trialId }] as const,
    detail: (id: number) => [...kitQueryKeys.shipments.all, 'detail', id] as const,
  },
  stock: {
    all: ['kits', 'stock'] as const,
    byTrial: (query: KitStockQuery) =>
      [...kitQueryKeys.stock.all, query] as const,
  },
  basket: {
    all: ['kits', 'basket'] as const,
    lists: () => [...kitQueryKeys.basket.all, 'list'] as const,
    list: (query: KitBasketQuery = {}) =>
      [...kitQueryKeys.basket.lists(), query] as const,
  },
  protocols: {
    all: ['kits', 'out-protocols'] as const,
    lists: () => [...kitQueryKeys.protocols.all, 'list'] as const,
    list: (query: KitOutProtocolListQuery) =>
      [...kitQueryKeys.protocols.lists(), query] as const,
    detail: (id: number) => [...kitQueryKeys.protocols.all, 'detail', id] as const,
  },
  corrections: {
    all: ['kits', 'corrections'] as const,
    lists: () => [...kitQueryKeys.corrections.all, 'list'] as const,
    list: (query: KitCorrectionListQuery) =>
      [...kitQueryKeys.corrections.lists(), query] as const,
  },
  demandForecast: {
    all: ['kits', 'demand-forecast'] as const,
    byTrial: (query: KitDemandForecastQuery) => [...kitQueryKeys.demandForecast.all, query] as const,
  },
};
